import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useMemo, useState } from "react";
import type { BrowserData, ClipboardItem } from "../types";

interface StorageStateViewerProps {
  storageStatePath: string;
  clipboard?: ClipboardItem[];
}

const isSensitiveKey = (key: string): boolean => {
  const lowerKey = key.toLowerCase();
  return lowerKey.includes("token") || lowerKey.includes("sess");
};

const MASKED_VALUE = "••••••••••••••••";

export const StorageStateViewer: React.FC<StorageStateViewerProps> = ({
  storageStatePath,
  clipboard,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState<
    "cookies" | "localStorage" | "sessionStorage" | "clipboard" | "indexedDB"
  >("cookies");
  const [maskedValues, setMaskedValues] = useState<Record<string, boolean>>({});

  const { data, isLoading, error } = useQuery<BrowserData | null, Error>({
    queryKey: ["storage-state", storageStatePath],
    queryFn: async () => {
      const response = await fetch(storageStatePath);
      if (response.status === 404) {
        // Storage state not available yet (test still running or never saved)
        return null;
      }
      if (!response.ok) {
        throw new Error(
          `Failed to fetch storageState data: ${response.status} ${response.statusText}`,
        );
      }
      return response.json();
    },
    enabled: !!storageStatePath,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  const toggleMaskedValue = (id: string) => {
    setMaskedValues((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const isMasked = (id: string): boolean => {
    return maskedValues[id] !== true; // Default to masked
  };

  const handleDownload = () => {
    window.open(storageStatePath, "_blank");
  };

  const filteredCookies = useMemo(() => {
    if (!data?.cookies) return [];
    return data.cookies.filter((cookie) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        cookie.name.toLowerCase().includes(searchLower) ||
        cookie.domain.toLowerCase().includes(searchLower) ||
        cookie.path.toLowerCase().includes(searchLower) ||
        cookie.value.toLowerCase().includes(searchLower)
      );
    });
  }, [data?.cookies, searchTerm]);

  const filteredLocalStorage = useMemo(() => {
    if (!data?.origins) return [];
    return data.origins
      .map((origin) => ({
        ...origin,
        localStorage: origin.localStorage.filter((item) => {
          const searchLower = searchTerm.toLowerCase();
          return (
            item.name.toLowerCase().includes(searchLower) ||
            item.value.toLowerCase().includes(searchLower) ||
            origin.origin.toLowerCase().includes(searchLower)
          );
        }),
      }))
      .filter((origin) => origin.localStorage.length > 0);
  }, [data?.origins, searchTerm]);

  const localStorageStats = useMemo(() => {
    if (!data?.origins) return { origins: 0, items: 0 };
    return {
      origins: data.origins.length,
      items: data.origins.reduce((sum, origin) => sum + origin.localStorage.length, 0),
    };
  }, [data?.origins]);

  // Session storage filtering
  const filteredSessionStorage = useMemo(() => {
    if (!data?.sessionStorage) return [];
    const searchLower = searchTerm.toLowerCase();

    return Object.entries(data.sessionStorage)
      .map(([origin, items]) => ({
        origin,
        items: Object.entries(items).filter(
          ([key, value]) =>
            key.toLowerCase().includes(searchLower) ||
            value.toLowerCase().includes(searchLower) ||
            origin.toLowerCase().includes(searchLower),
        ),
      }))
      .filter((entry) => entry.items.length > 0);
  }, [data?.sessionStorage, searchTerm]);

  // Session storage stats
  const sessionStorageStats = useMemo(() => {
    if (!data?.sessionStorage) return { origins: 0, items: 0 };
    const origins = Object.keys(data.sessionStorage).length;
    const items = Object.values(data.sessionStorage).reduce(
      (sum, obj) => sum + Object.keys(obj).length,
      0,
    );
    return { origins, items };
  }, [data?.sessionStorage]);

  const indexedDBStats = useMemo(() => {
    if (!data?.origins) return { databases: 0, stores: 0, records: 0 };
    let databaseCount = 0;
    let storeCount = 0;
    let recordCount = 0;

    data.origins.forEach((origin) => {
      if (origin.indexedDB) {
        databaseCount += origin.indexedDB.length;
        origin.indexedDB.forEach((db) => {
          storeCount += db.stores.length;
          db.stores.forEach((store) => {
            recordCount += store.records.length;
          });
        });
      }
    });

    return {
      databases: databaseCount,
      stores: storeCount,
      records: recordCount,
    };
  }, [data?.origins]);

  const filteredIndexedDB = useMemo(() => {
    if (!data?.origins) return [];
    const searchLower = searchTerm.toLowerCase();

    return data.origins
      .map((origin) => ({
        ...origin,
        indexedDB: origin.indexedDB
          ?.map((db) => ({
            ...db,
            stores: db.stores
              .map((store) => ({
                ...store,
                records: store.records.filter((record) => {
                  if (!record.value) return false;
                  const recordStr = JSON.stringify(record.value)?.toLowerCase();
                  return (
                    recordStr.includes(searchLower) ||
                    store.name?.toLowerCase().includes(searchLower) ||
                    db.name?.toLowerCase().includes(searchLower)
                  );
                }),
              }))
              .filter((store) => store.records.length > 0),
          }))
          .filter((db) => db.stores.length > 0),
      }))
      .filter((origin) => origin.indexedDB && origin.indexedDB.length > 0);
  }, [data?.origins, searchTerm]);

  const filteredClipboard = useMemo(() => {
    if (!clipboard) return [];
    const searchLower = searchTerm.toLowerCase();
    return clipboard.filter((item) => {
      return (
        item.content.toLowerCase().includes(searchLower) ||
        item.page_url.toLowerCase().includes(searchLower) ||
        item.operation.toLowerCase().includes(searchLower)
      );
    });
  }, [clipboard, searchTerm]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <CircularProgress />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
          Loading storage state...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Failed to load storage state
        </Typography>
        <Typography variant="body2">
          {error instanceof Error ? error.message : String(error)}
        </Typography>
      </Alert>
    );
  }

  if (!data) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
        No storage state available for this run.
      </Typography>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header with search and download */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <TextField
          label="Search storage state"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
          }}
        />
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          size="small"
        >
          Download
        </Button>
      </Stack>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, value) => setSelectedTab(value)}
          aria-label="storage state tabs"
        >
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Cookies</span>
                <Chip
                  label={filteredCookies.length}
                  size="small"
                  sx={{ height: 18, fontSize: "0.7rem" }}
                />
              </Stack>
            }
            value="cookies"
          />
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Local Storage</span>
                <Chip
                  label={localStorageStats.items}
                  size="small"
                  sx={{ height: 18, fontSize: "0.7rem" }}
                />
              </Stack>
            }
            value="localStorage"
          />
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Session Storage</span>
                <Chip
                  label={sessionStorageStats.items}
                  size="small"
                  sx={{ height: 18, fontSize: "0.7rem" }}
                />
              </Stack>
            }
            value="sessionStorage"
          />
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Clipboard</span>
                <Chip
                  label={filteredClipboard.length}
                  size="small"
                  sx={{ height: 18, fontSize: "0.7rem" }}
                />
              </Stack>
            }
            value="clipboard"
          />
          <Tab
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>IndexedDB</span>
                <Chip
                  label={indexedDBStats.records}
                  size="small"
                  sx={{ height: 18, fontSize: "0.7rem" }}
                />
              </Stack>
            }
            value="indexedDB"
          />
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {selectedTab === "cookies" && (
          <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            {filteredCookies.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No cookies found.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: "600px" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Value</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Domain</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Path</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Expires</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Flags</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCookies.map((cookie, index) => {
                      const cookieId = `cookie-${index}-${cookie.name}`;
                      const isSensitive = isSensitiveKey(cookie.name);
                      const shouldMask = isSensitive && isMasked(cookieId);

                      return (
                        <TableRow key={cookieId} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.85em",
                              }}
                            >
                              {cookie.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.85em",
                                  wordBreak: "break-all",
                                  maxWidth: "300px",
                                }}
                              >
                                {shouldMask ? MASKED_VALUE : cookie.value}
                              </Typography>
                              {isSensitive && (
                                <Tooltip title={shouldMask ? "Show value" : "Hide value"}>
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleMaskedValue(cookieId)}
                                  >
                                    {shouldMask ? (
                                      <VisibilityIcon fontSize="small" />
                                    ) : (
                                      <VisibilityOffIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.85em",
                              }}
                            >
                              {cookie.domain}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.85em",
                              }}
                            >
                              {cookie.path}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: "0.85em" }}>
                              {cookie.expires
                                ? new Date(cookie.expires * 1000).toLocaleString()
                                : "Session"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              {cookie.httpOnly && (
                                <Chip
                                  label="HttpOnly"
                                  size="small"
                                  color="info"
                                  sx={{
                                    height: 18,
                                    fontSize: "0.65rem",
                                  }}
                                />
                              )}
                              {cookie.secure && (
                                <Chip
                                  label="Secure"
                                  size="small"
                                  color="success"
                                  sx={{
                                    height: 18,
                                    fontSize: "0.65rem",
                                  }}
                                />
                              )}
                              <Chip
                                label={cookie.sameSite}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 18,
                                  fontSize: "0.65rem",
                                }}
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {selectedTab === "localStorage" && (
          <Stack spacing={2}>
            {filteredLocalStorage.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No local storage data found.
                </Typography>
              </Paper>
            ) : (
              filteredLocalStorage.map((origin, originIndex) => (
                <Paper
                  key={`${origin.origin}-${originIndex}`}
                  variant="outlined"
                  sx={{ overflow: "hidden" }}
                >
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "grey.100",
                      borderBottom: "1px solid",
                      borderBottomColor: "divider",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: "bold",
                          wordBreak: "break-all",
                        }}
                      >
                        {origin.origin}
                      </Typography>
                      <Chip
                        label={`${origin.localStorage.length} items`}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Key</TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {origin.localStorage.map((item, itemIndex) => {
                          const itemId = `ls-${originIndex}-${itemIndex}-${item.name}`;
                          const isSensitive = isSensitiveKey(item.name);
                          const shouldMask = isSensitive && isMasked(itemId);

                          return (
                            <TableRow key={itemId} hover>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.85em",
                                    fontWeight: "500",
                                  }}
                                >
                                  {item.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontSize: "0.85em",
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    {shouldMask ? MASKED_VALUE : item.value}
                                  </Typography>
                                  {isSensitive && (
                                    <Tooltip title={shouldMask ? "Show value" : "Hide value"}>
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleMaskedValue(itemId)}
                                      >
                                        {shouldMask ? (
                                          <VisibilityIcon fontSize="small" />
                                        ) : (
                                          <VisibilityOffIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ))
            )}
          </Stack>
        )}

        {selectedTab === "sessionStorage" && (
          <Stack spacing={2}>
            {filteredSessionStorage.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No session storage data found.
                </Typography>
              </Paper>
            ) : (
              filteredSessionStorage.map((entry, entryIndex) => (
                <Paper
                  key={`${entry.origin}-${entryIndex}`}
                  variant="outlined"
                  sx={{ overflow: "hidden" }}
                >
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "grey.100",
                      borderBottom: "1px solid",
                      borderBottomColor: "divider",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontFamily: "monospace",
                          fontWeight: "bold",
                          wordBreak: "break-all",
                        }}
                      >
                        {entry.origin}
                      </Typography>
                      <Chip label={`${entry.items.length} items`} size="small" variant="outlined" />
                    </Stack>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Key</TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {entry.items.map(([key, value], itemIndex) => {
                          const itemId = `ss-${entryIndex}-${itemIndex}-${key}`;
                          const isSensitive = isSensitiveKey(key);
                          const shouldMask = isSensitive && isMasked(itemId);

                          return (
                            <TableRow key={itemId} hover>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.85em",
                                    fontWeight: "500",
                                  }}
                                >
                                  {key}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontSize: "0.85em",
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    {shouldMask ? MASKED_VALUE : value}
                                  </Typography>
                                  {isSensitive && (
                                    <Tooltip title={shouldMask ? "Show value" : "Hide value"}>
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleMaskedValue(itemId)}
                                      >
                                        {shouldMask ? (
                                          <VisibilityIcon fontSize="small" />
                                        ) : (
                                          <VisibilityOffIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ))
            )}
          </Stack>
        )}

        {selectedTab === "clipboard" && (
          <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            {filteredClipboard.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No clipboard operations found.
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: "600px" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Timestamp</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Operation</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Page URL</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Content</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredClipboard.map((item, index) => {
                      const clipboardId = `clipboard-${index}`;
                      const isSensitive =
                        item.content.toLowerCase().includes("token") ||
                        item.content.toLowerCase().includes("sess");
                      const shouldMask = isSensitive && isMasked(clipboardId);

                      return (
                        <TableRow key={clipboardId} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: "0.85em" }}>
                              {new Date(item.timestamp).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.operation}
                              size="small"
                              color={item.operation === "write" ? "primary" : "default"}
                              sx={{ height: 18, fontSize: "0.7rem" }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.85em",
                                maxWidth: "300px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.page_url}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.85em",
                                  wordBreak: "break-all",
                                  maxWidth: "400px",
                                }}
                              >
                                {shouldMask ? MASKED_VALUE : item.content}
                              </Typography>
                              {isSensitive && (
                                <Tooltip title={shouldMask ? "Show content" : "Hide content"}>
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleMaskedValue(clipboardId)}
                                  >
                                    {shouldMask ? (
                                      <VisibilityIcon fontSize="small" />
                                    ) : (
                                      <VisibilityOffIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {selectedTab === "indexedDB" && (
          <Stack spacing={2}>
            {filteredIndexedDB.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No IndexedDB data found.
                </Typography>
              </Paper>
            ) : (
              filteredIndexedDB.map((origin, originIndex) =>
                origin.indexedDB?.map((db, dbIndex) => (
                  <Paper
                    key={`${origin.origin}-${dbIndex}`}
                    variant="outlined"
                    sx={{ overflow: "hidden" }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        backgroundColor: "grey.100",
                        borderBottom: "1px solid",
                        borderBottomColor: "divider",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Stack direction="column" spacing={0.5}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: "bold",
                            }}
                          >
                            {db.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {origin.origin} • v{db.version}
                          </Typography>
                        </Stack>
                        <Chip
                          label={`${db.stores.reduce((sum, store) => sum + store.records.length, 0)} records`}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </Box>

                    {db.stores.map((store, storeIndex) => (
                      <Box key={`store-${storeIndex}`} sx={{ p: 2 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mb: 2 }}
                        >
                          <Stack direction="column" spacing={0.5}>
                            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                              {store.name}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              {store.autoIncrement && (
                                <Chip
                                  label="Auto Increment"
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: "0.6rem" }}
                                />
                              )}
                              <Chip
                                label={`Key: ${store.keyPath ? (Array.isArray(store.keyPath) ? store.keyPath.join(", ") : store.keyPath) : "none"}`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 16, fontSize: "0.6rem" }}
                              />
                            </Stack>
                          </Stack>
                          <Chip
                            label={`${store.records.length} records`}
                            size="small"
                            sx={{ height: 18, fontSize: "0.7rem" }}
                          />
                        </Stack>

                        {store.indexes.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mb: 1 }}
                            >
                              Indexes:
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                              {store.indexes.map((index, indexIndex) => (
                                <Chip
                                  key={indexIndex}
                                  label={`${index.name} (${index.keyPath ? (Array.isArray(index.keyPath) ? index.keyPath.join(", ") : index.keyPath) : "none"})${index.unique ? " • unique" : ""}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: 18,
                                    fontSize: "0.65rem",
                                    mb: 0.5,
                                  }}
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}

                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>Record</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {store.records.map((record, recordIndex) => {
                                const recordId = `idb-${originIndex}-${dbIndex}-${storeIndex}-${recordIndex}`;
                                const recordStr = JSON.stringify(record.value, null, 2);
                                const isSensitive =
                                  recordStr.toLowerCase().includes("token") ||
                                  recordStr.toLowerCase().includes("sess");
                                const shouldMask = isSensitive && isMasked(recordId);

                                return (
                                  <TableRow key={recordId} hover>
                                    <TableCell>
                                      <Stack direction="row" spacing={1} alignItems="flex-start">
                                        <Box
                                          sx={{
                                            flex: 1,
                                            fontFamily: "monospace",
                                            fontSize: "0.85em",
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-all",
                                            backgroundColor: "grey.100",
                                            p: 1,
                                            borderRadius: 1,
                                          }}
                                        >
                                          {shouldMask ? MASKED_VALUE : recordStr}
                                        </Box>
                                        {isSensitive && (
                                          <Tooltip title={shouldMask ? "Show value" : "Hide value"}>
                                            <IconButton
                                              size="small"
                                              onClick={() => toggleMaskedValue(recordId)}
                                            >
                                              {shouldMask ? (
                                                <VisibilityIcon fontSize="small" />
                                              ) : (
                                                <VisibilityOffIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    ))}
                  </Paper>
                )),
              )
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
};
